import type { JSX } from 'react';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useJobPosition } from '../hooks/useJobPosition';
import { useJobPositionCandidates } from '../hooks/useJobPositionCandidates';
import { useSelectableCandidates } from '../hooks/useSelectableCandidates';
import { JobPositionStatusBadge } from '../components/JobPositionStatusBadge';
import { JobPositionCandidatesList } from '../components/candidates/JobPositionCandidatesList';
import { AddCandidateDialog } from '../components/candidates/AddCandidateDialog';
import type { JobPosition } from '../types/jobPosition.types';

function PositionDetailsCard({ jobPosition }: { jobPosition: JobPosition }): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Position Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {jobPosition.description && (
          <div>
            <h4 className="font-medium mb-1">Description</h4>
            <p className="text-muted-foreground">{jobPosition.description}</p>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Employment Type</span>
            <p className="font-medium">{jobPosition.employmentType?.replace('_', ' ') ?? 'N/A'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Remote Policy</span>
            <p className="font-medium">{jobPosition.remotePolicy ?? 'N/A'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Department</span>
            <p className="font-medium">{jobPosition.department ?? 'N/A'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Location</span>
            <p className="font-medium">{jobPosition.location}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function JobPositionDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const { jobPosition, isLoading, isError } = useJobPosition(id);
  const {
    candidates: linkedCandidates,
    hasData: hasCandidatesData,
    isLoading: isCandidatesLoading,
    isError: isCandidatesError,
  } = useJobPositionCandidates(id);
  const { candidates: selectableCandidates, isLoading: isSelectableCandidatesLoading } =
    useSelectableCandidates(jobPosition?.companyId);

  const handleBack = (): void => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="container py-6">
        <div className="text-center py-8 text-muted-foreground">Loading job position...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container py-6">
        <div className="text-center py-8 text-destructive">Error loading job position</div>
      </div>
    );
  }

  if (!jobPosition) {
    return (
      <div className="container py-6">
        <div className="text-center py-8 text-muted-foreground">Job position not found</div>
      </div>
    );
  }

  const linkedCandidateIds = linkedCandidates.map((c) => c.candidate.id);

  return (
    <div className="container py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{jobPosition.title}</h1>
            <JobPositionStatusBadge status={jobPosition.status} />
          </div>
          <p className="text-muted-foreground">
            {jobPosition.department && `${jobPosition.department} • `}
            {jobPosition.location}
          </p>
        </div>
      </div>

      {/* Job Details Card */}
      <PositionDetailsCard jobPosition={jobPosition} />

      {/* Candidates Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Candidates</CardTitle>
            <CardDescription>
              {linkedCandidates.length} candidate{linkedCandidates.length !== 1 ? 's' : ''} linked
              to this position
            </CardDescription>
          </div>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add Candidate
          </Button>
        </CardHeader>
        <CardContent>
          <JobPositionCandidatesList
            positionId={id!}
            candidates={linkedCandidates}
            hasData={hasCandidatesData}
            isLoading={isCandidatesLoading}
            isError={isCandidatesError}
          />
        </CardContent>
      </Card>

      {/* Add Candidate Dialog */}
      <AddCandidateDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        positionId={id!}
        candidates={selectableCandidates}
        isCandidatesLoading={isSelectableCandidatesLoading}
        linkedCandidateIds={linkedCandidateIds}
      />
    </div>
  );
}
